import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "list", "dropzone", "deletedFiles"]
  static values = {
    clipboardIcon: String,
    checkIcon: String,
    downloadIcon: String,
    trashIcon: String,
    jpgIcon: String,
    pdfIcon: String,
    wordIcon: String,
    fieldName: String
  }

  // MIME type mappings for file limit validation by type
  static fileTypeMappings = {
    'image': ['image/jpeg', 'image/png', 'image/gif', 'image/heic'],
    'pdf': ['application/pdf'],
    'doc': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'],
    'spreadsheet': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']
  }

  connect() {
    // Store accumulated files in a DataTransfer object
    this.accumulatedFiles = new DataTransfer()
  }

  get acceptedTypes() {
    const accept = this.inputTarget.accept
    if (!accept) return null
    return accept.split(',').map(type => type.trim().toLowerCase())
  }

  isFileAccepted(file) {
    const acceptedTypes = this.acceptedTypes
    if (!acceptedTypes) return true

    const fileName = file.name.toLowerCase()
    const fileType = file.type.toLowerCase()
    const fileExtension = '.' + fileName.split('.').pop()

    return acceptedTypes.some(accepted => {
      // Check MIME type (e.g., "image/jpeg")
      if (accepted === fileType) return true
      
      // Check file extension (e.g., ".pdf")
      if (accepted.startsWith('.') && fileName.endsWith(accepted)) return true
      
      // Check wildcard MIME types (e.g., "image/*")
      if (accepted.endsWith('/*')) {
        const baseType = accepted.slice(0, -2)
        if (fileType.startsWith(baseType + '/')) return true
      }
      
      return false
    })
  }

  filterAcceptedFiles(files) {
    return Array.from(files).filter(file => this.isFileAccepted(file))
  }

  openFilePicker(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  handleFiles(event) {
    const files = event.target.files
    this.addFiles(files)
  }

  addFiles(files) {
    const limit = this.inputTarget.dataset.fileLimit ? parseInt(this.inputTarget.dataset.fileLimit) : null
    const limitMode = this.inputTarget.dataset.fileLimitMode || 'total'
    
    // Filter accepted files first
    const acceptedFiles = this.filterAcceptedFiles(files)
    
    // Trim files to fit within limits
    const filesToAdd = limit ? this.trimFilesToLimit(acceptedFiles, limit, limitMode) : acceptedFiles
    
    // Add files to accumulated collection
    filesToAdd.forEach(file => {
      this.accumulatedFiles.items.add(file)
    })
    
    // Update the input's files to include all accumulated files
    this.inputTarget.files = this.accumulatedFiles.files
    
    // Update the visual list (only show files that were actually added)
    this.updateList(filesToAdd)
  }

  trimFilesToLimit(files, limit, limitMode) {
    const result = []
    
    if (limitMode === 'total') {
      const currentCount = this.accumulatedFiles.files.length + 
        this.listTarget.querySelectorAll('.file-upload-item[data-existing="true"]').length
      const availableSlots = Math.max(0, limit - currentCount)
      
      return files.slice(0, availableSlots)
    } else if (limitMode === 'per_type') {
      // Track counts per type during this selection
      const typeCountsThisSelection = {}
      
      for (const file of files) {
        const typeKey = this.getFileTypeKey(file)
        if (!typeKey) {
          // Unknown type, allow it
          result.push(file)
          continue
        }
        
        // Initialize count for this type if not already
        if (!(typeKey in typeCountsThisSelection)) {
          typeCountsThisSelection[typeKey] = 0
        }
        
        // Get existing count for this type
        const existingCount = this.countFilesByType(typeKey) + typeCountsThisSelection[typeKey]
        
        if (existingCount < limit) {
          result.push(file)
          typeCountsThisSelection[typeKey]++
        }
      }
      
      return result
    }
    
    return files
  }

  countFilesByType(typeKey) {
    // Count accumulated files of this type
    const accumulatedCount = Array.from(this.accumulatedFiles.files)
      .filter(f => this.getFileTypeKey(f) === typeKey).length
    
    // Count existing files of this type
    const existingCount = this.countExistingFilesByType(typeKey)
    
    return accumulatedCount + existingCount
  }

  canAddFile(file, limit, limitMode, allowedTypeKeys) {
    const currentFiles = Array.from(this.accumulatedFiles.files)
    const existingFiles = this.listTarget.querySelectorAll('.file-upload-item[data-existing="true"]').length
    
    if (limitMode === 'total') {
      // Total limit applies to all files combined
      return (currentFiles.length + existingFiles) < limit
    } else if (limitMode === 'per_type') {
      // Per-type limit: count files of the same type category
      const fileTypeKey = this.getFileTypeKey(file)
      if (!fileTypeKey) return true // Unknown type, allow it
      
      const sameTypeCount = currentFiles.filter(f => this.getFileTypeKey(f) === fileTypeKey).length
      const existingSameType = this.countExistingFilesByType(fileTypeKey)
      
      return (sameTypeCount + existingSameType) < limit
    }
    return true
  }

  getFileTypeKey(file) {
    const mimeType = file.type.toLowerCase()
    for (const [key, mimeTypes] of Object.entries(this.constructor.fileTypeMappings)) {
      if (mimeTypes.includes(mimeType)) {
        return key
      }
    }
    return null
  }

  countExistingFilesByType(typeKey) {
    const existingItems = this.listTarget.querySelectorAll('.file-upload-item[data-existing="true"]')
    let count = 0
    existingItems.forEach(item => {
      const fileName = item.querySelector('.file-info-name')?.textContent || ''
      const ext = fileName.split('.').pop()?.toLowerCase()
      const mimeType = this.getMimeTypeFromExtension(ext)
      if (mimeType && this.getFileTypeKey({ type: mimeType }) === typeKey) {
        count++
      }
    })
    return count
  }

  getMimeTypeFromExtension(ext) {
    const extToMime = {
      'jpg': 'image/jpeg', 'jpeg': 'image/jpeg', 'png': 'image/png', 'gif': 'image/gif', 'heic': 'image/heic',
      'pdf': 'application/pdf',
      'doc': 'application/msword', 'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel', 'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
    return extToMime[ext] || null
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  dragEnter(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add('drag-over')
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('drag-over')
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('drag-over')
    
    const files = this.filterAcceptedFiles(event.dataTransfer.files)
    if (files.length > 0) {
      this.addFiles(files)
    }
  }

  updateList(files) {
    if (!this.hasListTarget) return

    // Add new file items
    Array.from(files).forEach((file, index) => {
      const item = document.createElement('div')
      item.classList.add('file-upload-item')
      item.dataset.newFile = 'true'
      
      // Calculate the index of this file in the accumulated files
      const fileIndex = this.accumulatedFiles.files.length - files.length + index
      
      // Create a temporary URL for the file to allow preview/download
      const fileUrl = URL.createObjectURL(file)
      
      item.dataset.fileIndex = fileIndex
      item.innerHTML = `
        <div class="file-item-icon">
          <img src="${this.getFileIcon(file.name)}" alt="">
        </div>
        <div class='file-item-details'>
          <div class="file-item-info">
            <span class="file-name">${this.escapeHtml(file.name)}</span>
            <span class="file-status done"><img src="${this.checkIconValue}" alt=""> Done</span>
          </div>
          <div class="file-item-actions">
            <a href="${fileUrl}" download="${this.escapeHtml(file.name)}" class="file-action-btn" title="Download"><img src="${this.downloadIconValue}" alt="Download"></a>
            <button type="button" class="file-action-btn" data-action="click->file-upload#removeFile"><img src="${this.trashIconValue}" alt="Remove"></button>
          </div>
        </div>
      `
      
      this.listTarget.appendChild(item)
    })
  }

  removeFile(event) {
    event.preventDefault()
    const item = event.target.closest('.file-upload-item')
    if (item) {
      const fileIndex = parseInt(item.dataset.fileIndex, 10)
      
      // Remove file from accumulated files
      if (!isNaN(fileIndex)) {
        this.removeFileAtIndex(fileIndex)
        // Update indices for remaining items
        this.updateFileIndices()
      }
      
      item.remove()
    }
  }

  removeFileAtIndex(indexToRemove) {
    const newDataTransfer = new DataTransfer()
    const files = this.accumulatedFiles.files
    
    for (let i = 0; i < files.length; i++) {
      if (i !== indexToRemove) {
        newDataTransfer.items.add(files[i])
      }
    }
    
    this.accumulatedFiles = newDataTransfer
    this.inputTarget.files = this.accumulatedFiles.files
  }

  updateFileIndices() {
    // Update data-file-index on remaining new file items
    const items = this.listTarget.querySelectorAll('.file-upload-item[data-new-file="true"]')
    items.forEach((item, index) => {
      item.dataset.fileIndex = index
    })
  }

  removeExistingFile(event) {
    event.preventDefault()
    const item = event.target.closest('.file-upload-item')
    if (item) {
      item.remove()
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getFileIcon(filename) {
    const ext = filename.split('.').pop().toLowerCase()
    
    if (ext === 'jpg' || ext === 'jpeg') {
      return this.jpgIconValue || this.clipboardIconValue
    } else if (ext === 'pdf') {
      return this.pdfIconValue || this.clipboardIconValue
    } else if (ext === 'doc' || ext === 'docx') {
      return this.wordIconValue || this.clipboardIconValue
    }
    
    return this.clipboardIconValue
  }
}
